import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Loader2,
  Sparkles,
  Check,
  X as XIcon,
  Clock,
  ExternalLink,
  AlertTriangle,
  Compass,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import AdminShell from "../components/AdminShell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AdminBrandDiscovery — /admin/brand-discovery
 *
 * Agentic complement to /admin/products/seed:
 *   • Seeder asks: "Find 8 hydrating concealers on Amazon" (brief-driven)
 *   • Discovery asks: "Scan the open web for NEW trans-affirming brands
 *                      we don't have yet" (autonomous, theme-driven)
 *
 * Pipeline visible to the operator:
 *   Tavily (10 queries) → dedupe by domain → strip known catalog brands
 *   → GPT-5.2 LLM-judge scores each on the why_inclusive rubric
 *   → survivors land in this review queue
 *
 * Founder makes the call: Approve, Reject, or Park (decide later).
 */

const BUCKET_META = {
  explicitly_affirming: {
    label: "Explicitly affirming",
    color: "text-emerald-300 bg-emerald-500/10 border-emerald-400/30",
  },
  inclusive_general: {
    label: "Inclusive (general)",
    color: "text-amber-200 bg-amber-500/10 border-amber-400/30",
  },
};

const BrandDiscoveryPage = () => {
  const [tab, setTab] = useState("pending"); // 'pending' | 'promoted'
  const [sweepRunning, setSweepRunning] = useState(false);
  const [lastSweep, setLastSweep] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(true);
  const [bucketFilter, setBucketFilter] = useState("");
  const [error, setError] = useState(null);
  const [recentSweeps, setRecentSweeps] = useState([]);
  const [promoted, setPromoted] = useState([]);
  const [promotedLoading, setPromotedLoading] = useState(false);

  const loadCandidates = async () => {
    setCandidatesLoading(true);
    try {
      const params = new URLSearchParams({ status_filter: "pending" });
      if (bucketFilter) params.append("bucket", bucketFilter);
      const { data } = await axios.get(
        `${API}/admin/brand-discovery/candidates?${params.toString()}`,
        { withCredentials: true },
      );
      setCandidates(data.items || []);
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          "Couldn't load brand candidates. Are you signed in as admin?",
      );
    } finally {
      setCandidatesLoading(false);
    }
  };

  const loadPromoted = async () => {
    setPromotedLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/brand-discovery/candidates?status_filter=approved&limit=200`,
        { withCredentials: true },
      );
      const brands = data.items || [];
      // For each brand pull its FREE catalog-ingested products in parallel
      const enriched = await Promise.all(
        brands.map(async (b) => {
          try {
            const { data: prodData } = await axios.get(
              `${API}/admin/brand-discovery/catalog-products?brand_candidate_id=${b.id}`,
              { withCredentials: true },
            );
            return { ...b, catalog_products: prodData.items || [] };
          } catch {
            return { ...b, catalog_products: [] };
          }
        }),
      );
      setPromoted(enriched);
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          "Couldn't load promoted brands.",
      );
    } finally {
      setPromotedLoading(false);
    }
  };

  const loadRecentSweeps = async () => {
    try {
      const { data } = await axios.get(
        `${API}/admin/brand-discovery/sweeps?limit=5`,
        { withCredentials: true },
      );
      setRecentSweeps(data.items || []);
    } catch {
      // non-critical — silently skip
    }
  };

  useEffect(() => {
    if (tab === "pending") {
      loadCandidates();
      loadRecentSweeps();
    } else if (tab === "promoted") {
      loadPromoted();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucketFilter, tab]);

  const runSweep = async () => {
    if (sweepRunning) return;
    setSweepRunning(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/brand-discovery/sweep`,
        { max_results_per_query: 8, max_candidates_judged: 25 },
        { withCredentials: true },
      );
      setLastSweep(data);
      toast.success(
        `${data.persisted} new brand${
          data.persisted === 1 ? "" : "s"
        } discovered · ${data.judged} judged`,
      );
      await loadCandidates();
      await loadRecentSweeps();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "Sweep failed.";
      if (status === 503) {
        setError(detail);
        toast.error("Tavily not configured");
      } else {
        toast.error(detail);
      }
    } finally {
      setSweepRunning(false);
    }
  };

  const decide = async (id, action, prompt = false) => {
    let note = null;
    if (prompt) {
      note = window.prompt(`Add a note for ${action}? (optional)`) || null;
    }
    try {
      const { data } = await axios.post(
        `${API}/admin/brand-discovery/candidates/${id}/${action}`,
        { note },
        { withCredentials: true },
      );
      setCandidates((prev) => prev.filter((c) => c.id !== id));
      const labels = { approve: "Approved", reject: "Rejected", park: "Parked" };
      if (action === "approve") {
        const discovered = data?.discovery?.persisted ?? 0;
        const total = data?.discovery?.products_total ?? 0;
        const platform = data?.discovery?.platform;
        const status = data?.discovery?.status;
        const error = data?.discovery?.error;
        if (status === "needs_url") {
          toast.success(
            "Approved · couldn't auto-detect a catalog URL — set it in /admin/brand-catalog-sync",
          );
        } else if (status === "skipped") {
          toast.success(
            `Approved · ${platform || "this platform"} needs a custom crawler (Phase 3)`,
          );
        } else if (error) {
          toast.error(`Approved · catalog sync failed: ${error}`);
        } else if (discovered === 0 && total === 0) {
          toast.success(
            "Approved · no products synced yet (you can retry later)",
          );
        } else if (discovered === 0) {
          toast.success(
            `Approved · ${total} product${
              total === 1 ? "" : "s"
            } refreshed in Trouvé's catalog ✨`,
          );
        } else {
          toast.success(
            `Approved · ${discovered} new product${
              discovered === 1 ? "" : "s"
            } now live in Trouvé's catalog ✨`,
          );
        }
      } else {
        toast.success(labels[action] || "Done");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${action} failed`);
    }
  };

  return (
    <AdminShell
      title="Brand discovery"
      eyebrow="Agentic"
      subtitle="Autonomously scan the open web for new trans-affirming brand drops, vet them against our affirming-criteria rubric, and grow the catalog with one tap."
    >
      <div className="max-w-6xl space-y-8">
        {/* ─── Tab nav ─── */}
        <div
          className="flex items-center gap-1 border-b border-white/8 -mt-2"
          data-testid="brand-discovery-tabs"
        >
          <TabButton
            value="pending"
            label="Pending review"
            active={tab === "pending"}
            count={candidates.length}
            onClick={() => setTab("pending")}
          />
          <TabButton
            value="promoted"
            label="Promoted to catalog"
            active={tab === "promoted"}
            count={promoted.length}
            onClick={() => setTab("promoted")}
          />
        </div>

        {tab === "pending" && (
          <>
        {/* ─── Sweep trigger panel ─── */}
        <section className="bg-[#1A1720] border border-white/8 rounded-2xl p-6 space-y-5">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#D4B483] to-[#9B6A6C] flex items-center justify-center shrink-0">
              <Compass className="h-5 w-5 text-[#1A1720]" />
            </div>
            <div className="flex-1">
              <h2 className="text-white text-lg font-serif italic">
                Run a discovery sweep
              </h2>
              <p className="text-white/55 text-sm mt-0.5 leading-relaxed">
                10 curated queries · Tavily advanced search · GPT-5.2 LLM-judge
                scores against `why_inclusive` rubric. Known catalog brands
                are auto-stripped. ~20 Tavily credits per sweep.
              </p>
            </div>
            <button
              onClick={runSweep}
              disabled={sweepRunning}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#D4B483] hover:bg-[#E0C295] disabled:opacity-30 disabled:cursor-not-allowed text-[#1A1720] rounded-full font-medium text-sm transition"
              data-testid="brand-discovery-sweep-btn"
            >
              {sweepRunning ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Scanning the web…
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  Run sweep
                </>
              )}
            </button>
          </div>

          {/* Last sweep summary */}
          {lastSweep && (
            <div
              className="grid grid-cols-2 sm:grid-cols-5 gap-3 pt-4 border-t border-white/5"
              data-testid="brand-discovery-last-result"
            >
              <SummaryStat label="Queries" value={lastSweep.queries} />
              <SummaryStat label="Raw results" value={lastSweep.raw_results} />
              <SummaryStat
                label="Unique candidates"
                value={lastSweep.unique_candidates}
              />
              <SummaryStat label="Judged" value={lastSweep.judged} />
              <SummaryStat
                label="New in queue"
                value={lastSweep.persisted}
                accent
              />
            </div>
          )}
        </section>

        {/* ─── Config error banner ─── */}
        {error && (
          <div
            className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-400/30 rounded-xl text-red-200 text-sm"
            data-testid="brand-discovery-config-error"
          >
            <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
            <div>
              <div className="font-medium mb-1">Setup needed</div>
              <div className="text-red-200/80 text-xs leading-relaxed">
                {error}
              </div>
            </div>
          </div>
        )}

        {/* ─── Filter + queue ─── */}
        <section>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <h2 className="text-white text-lg font-serif italic">
                Pending review
                {candidates.length > 0 && (
                  <span className="text-[#D4B483] ml-2 text-base">
                    ({candidates.length})
                  </span>
                )}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <BucketFilterChip
                value=""
                label="All"
                active={bucketFilter === ""}
                onClick={() => setBucketFilter("")}
              />
              <BucketFilterChip
                value="explicitly_affirming"
                label="Explicitly affirming"
                active={bucketFilter === "explicitly_affirming"}
                onClick={() => setBucketFilter("explicitly_affirming")}
              />
              <BucketFilterChip
                value="inclusive_general"
                label="Inclusive (general)"
                active={bucketFilter === "inclusive_general"}
                onClick={() => setBucketFilter("inclusive_general")}
              />
              <button
                onClick={loadCandidates}
                className="text-white/50 hover:text-white/80 p-1.5"
                title="Refresh"
                data-testid="brand-discovery-refresh-btn"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>

          {candidatesLoading ? (
            <div className="flex items-center justify-center py-12 text-white/40">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : candidates.length === 0 ? (
            <div className="text-center py-12 text-white/40 text-sm bg-[#1A1720]/50 border border-white/5 rounded-2xl">
              No pending candidates. Run a sweep above to discover new brands.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {candidates.map((c) => (
                <CandidateCard
                  key={c.id}
                  candidate={c}
                  onApprove={() => decide(c.id, "approve")}
                  onReject={() => decide(c.id, "reject", true)}
                  onPark={() => decide(c.id, "park")}
                />
              ))}
            </div>
          )}
        </section>

        {/* ─── Recent sweeps history ─── */}
        {recentSweeps.length > 0 && (
          <section
            className="mt-12 pt-6 border-t border-white/5"
            data-testid="brand-discovery-sweep-history"
          >
            <h3 className="text-white/55 text-xs uppercase tracking-[0.18em] mb-3">
              Recent sweeps
            </h3>
            <div className="space-y-2">
              {recentSweeps.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center justify-between bg-[#1A1720]/60 border border-white/5 rounded-xl px-4 py-2.5 text-xs text-white/60"
                >
                  <div className="flex items-center gap-3">
                    <Clock className="h-3.5 w-3.5 text-white/30" />
                    <span>
                      {new Date(s.started_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span>{s.queries} queries</span>
                    <span>{s.unique_candidates} candidates</span>
                    <span className="text-emerald-300/80">
                      +{s.persisted} new
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
          </>
        )}

        {tab === "promoted" && (
          <PromotedBrandsPanel
            promoted={promoted}
            loading={promotedLoading}
            onRediscover={async (cid) => {
              const t = toast.loading("Re-syncing catalog…");
              try {
                const { data } = await axios.post(
                  `${API}/admin/brand-discovery/candidates/${cid}/rediscover`,
                  {},
                  { withCredentials: true },
                );
                const status = data?.status;
                const persisted = data?.persisted ?? 0;
                const total = data?.products_total ?? 0;
                if (status === "needs_url") {
                  toast.error("No catalog URL on file", { id: t });
                } else if (status === "skipped") {
                  toast.error(
                    `${data?.platform || "Platform"} needs a custom crawler`,
                    { id: t },
                  );
                } else if (status === "error") {
                  toast.error(data?.error || "Sync failed", { id: t });
                } else {
                  toast.success(
                    `${persisted} new · ${total} total in catalog`,
                    { id: t },
                  );
                }
                await loadPromoted();
              } catch (e) {
                toast.error(
                  e?.response?.data?.detail || "Re-sync failed",
                  { id: t },
                );
              }
            }}
            onToggleProduct={async (pid) => {
              try {
                const { data } = await axios.post(
                  `${API}/admin/brand-discovery/catalog-products/${pid}/toggle`,
                  {},
                  { withCredentials: true },
                );
                toast.success(
                  data.is_active
                    ? "Live in Trouvé's catalog"
                    : "Hidden from Trouvé",
                );
                await loadPromoted();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Toggle failed");
              }
            }}
          />
        )}
      </div>
    </AdminShell>
  );
};

const SummaryStat = ({ label, value, accent }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.18em] text-white/35">
      {label}
    </div>
    <div
      className={`text-2xl font-light ${
        accent ? "text-[#D4B483]" : "text-white"
      } mt-0.5`}
    >
      {value}
    </div>
  </div>
);

const BucketFilterChip = ({ value, label, active, onClick }) => (
  <button
    onClick={onClick}
    data-testid={`brand-discovery-bucket-filter-${value || "all"}`}
    className={`text-xs px-3 py-1.5 rounded-full border transition ${
      active
        ? "bg-[#D4B483]/20 text-[#D4B483] border-[#D4B483]/40"
        : "bg-white/5 text-white/55 border-white/10 hover:bg-white/10"
    }`}
  >
    {label}
  </button>
);

const CandidateCard = ({ candidate, onApprove, onReject, onPark }) => {
  const meta = BUCKET_META[candidate.bucket] || BUCKET_META.inclusive_general;
  return (
    <article
      className="bg-[#1A1720] border border-white/8 rounded-2xl p-5 flex flex-col gap-3"
      data-testid="brand-discovery-card"
      data-candidate-id={candidate.id}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span
              className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${meta.color}`}
            >
              {meta.label}
            </span>
            <span className="text-[10px] text-white/30 font-mono">
              {candidate.affirming_score}/100
            </span>
          </div>
          <h3 className="text-white text-base font-medium leading-snug truncate">
            {candidate.brand}
          </h3>
          <a
            href={candidate.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-white/50 hover:text-[#D4B483] transition mt-0.5"
          >
            {candidate.domain}
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
        {candidate.suggested_category && (
          <span className="text-[10px] uppercase tracking-wider text-[#D4B483] bg-[#D4B483]/10 px-2 py-1 rounded-full shrink-0">
            {candidate.suggested_category}
          </span>
        )}
      </div>

      {candidate.founder_identity && (
        <div className="text-[11px] text-amber-200/80">
          <span className="text-white/40">Founder: </span>
          {candidate.founder_identity}
        </div>
      )}

      <p className="text-white/65 text-sm leading-relaxed">
        {candidate.reasoning}
      </p>

      {candidate.why_inclusive && (
        <div className="text-[11px] text-white/45 bg-white/[0.03] border-l-2 border-[#D4B483]/40 pl-3 py-1.5 italic">
          {candidate.why_inclusive}
        </div>
      )}

      {candidate.snippets && candidate.snippets.length > 0 && (
        <details className="text-[11px] text-white/45">
          <summary className="cursor-pointer hover:text-white/65 transition">
            Tavily snippets ({candidate.snippets.length})
          </summary>
          <ul className="mt-1 space-y-1 pl-4 list-disc">
            {candidate.snippets.map((s, i) => (
              <li key={i} className="leading-relaxed">
                {s.length > 200 ? `${s.slice(0, 200)}…` : s}
              </li>
            ))}
          </ul>
        </details>
      )}

      <div className="flex items-center justify-between mt-1 gap-2 pt-2 border-t border-white/5">
        <button
          onClick={onPark}
          className="text-[11px] text-white/45 hover:text-white/80 transition"
          data-testid="brand-discovery-card-park-btn"
        >
          Park for later
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={onReject}
            className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/5 hover:bg-red-500/15 text-white/55 hover:text-red-300 rounded-full text-xs transition"
            data-testid="brand-discovery-card-reject-btn"
          >
            <XIcon className="h-3 w-3" /> Reject
          </button>
          <button
            onClick={onApprove}
            className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 rounded-full text-xs font-medium transition"
            data-testid="brand-discovery-card-approve-btn"
          >
            <Check className="h-3 w-3" /> Approve
          </button>
        </div>
      </div>
    </article>
  );
};

const TabButton = ({ label, active, count, onClick, value }) => (
  <button
    onClick={onClick}
    data-testid={`brand-discovery-tab-${value}`}
    className={`relative px-4 py-3 text-sm font-medium transition border-b-2 -mb-px ${
      active
        ? "text-[#D4B483] border-[#D4B483]"
        : "text-white/45 border-transparent hover:text-white/75"
    }`}
  >
    {label}
    {count > 0 && (
      <span
        className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
          active
            ? "bg-[#D4B483]/15 text-[#D4B483]"
            : "bg-white/8 text-white/55"
        }`}
      >
        {count}
      </span>
    )}
  </button>
);

const PromotedBrandsPanel = ({
  promoted,
  loading,
  onRediscover,
  onToggleProduct,
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-white/40">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (!promoted.length) {
    return (
      <div
        className="text-center py-12 text-white/40 text-sm bg-[#1A1720]/50 border border-white/5 rounded-2xl"
        data-testid="brand-discovery-promoted-empty"
      >
        No promoted brands yet. Approve a candidate in the Pending tab to
        send it to Trouvé's catalog.
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="brand-discovery-promoted-list">
      {promoted.map((brand) => (
        <PromotedBrandCard
          key={brand.id}
          brand={brand}
          onRediscover={() => onRediscover(brand.id)}
          onToggleProduct={onToggleProduct}
        />
      ))}
    </div>
  );
};

const PromotedBrandCard = ({ brand, onRediscover, onToggleProduct }) => {
  const products = brand.catalog_products || [];
  const activeCount = products.filter((p) => p.is_active).length;
  return (
    <article
      className="bg-[#1A1720] border border-white/8 rounded-2xl overflow-hidden"
      data-testid="brand-discovery-promoted-card"
      data-brand-id={brand.id}
    >
      <header className="p-5 flex items-start justify-between gap-3 border-b border-white/5">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-wider text-emerald-300 bg-emerald-500/15 px-2 py-0.5 rounded-full border border-emerald-400/30">
              Live · {activeCount} active
            </span>
            <span className="text-[10px] text-white/30 font-mono">
              {brand.affirming_score}/100
            </span>
            {brand.auto_synced_platform && (
              <span className="text-[10px] uppercase tracking-wider text-sky-300 bg-sky-500/10 px-1.5 py-0.5 rounded">
                {brand.auto_synced_platform}
              </span>
            )}
          </div>
          <h3 className="text-white text-base font-medium">{brand.brand}</h3>
          <a
            href={brand.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-white/50 hover:text-[#D4B483] transition mt-0.5"
          >
            {brand.domain}
            <ExternalLink className="h-3 w-3" />
          </a>
          {brand.why_inclusive && (
            <p className="text-[11px] text-white/45 italic mt-2 max-w-2xl">
              {brand.why_inclusive}
            </p>
          )}
        </div>
        <button
          onClick={onRediscover}
          className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/70 rounded-full text-xs transition"
          data-testid="brand-discovery-rediscover-btn"
          title="Re-crawl this brand's full public catalog (free)"
        >
          <RefreshCw className="h-3 w-3" />
          Re-sync catalog
        </button>
      </header>

      {products.length === 0 ? (
        <div className="px-5 py-6 text-center text-white/45 text-sm">
          {brand.auto_synced_status === "skipped"
            ? `This platform (${brand.auto_synced_platform || "custom"}) needs a Phase 3 crawler — no products yet.`
            : brand.auto_synced_status === "needs_url"
            ? "No catalog URL on file. Add one in /admin/brand-catalog-sync."
            : 'No catalog products synced yet. Hit "Re-sync catalog" to import from the brand site.'}
        </div>
      ) : (
        <ul className="divide-y divide-white/5">
          {products.map((p) => (
            <li
              key={p.id}
              className={`px-5 py-3 flex items-center justify-between gap-3 ${
                p.is_active ? "" : "opacity-50"
              }`}
              data-testid="brand-discovery-promoted-product"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-[#D4B483] bg-[#D4B483]/10 px-1.5 py-0.5 rounded">
                    {p.category}
                  </span>
                  <span className="text-[10px] text-white/40 font-mono">
                    {p.price_band}
                  </span>
                  {p.ingredient_coverage && (
                    <span
                      className="text-[10px] uppercase tracking-wider text-emerald-300/80 bg-emerald-500/10 px-1.5 py-0.5 rounded"
                      title="INCI ingredient list parsed"
                    >
                      INCI
                    </span>
                  )}
                  {p.allergen_flag_count > 0 && (
                    <span
                      className="text-[10px] uppercase tracking-wider text-amber-300/80 bg-amber-500/10 px-1.5 py-0.5 rounded"
                      title={`${p.allergen_flag_count} allergen / sensitivity flag(s)`}
                    >
                      {p.allergen_flag_count} flag{p.allergen_flag_count === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
                <p className="text-white text-sm mt-0.5 truncate">{p.name}</p>
                {p.url && (
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[10px] text-white/40 hover:text-[#D4B483] transition"
                  >
                    View on brand site
                    <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                )}
              </div>
              <button
                onClick={() => onToggleProduct(p.id)}
                className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs transition ${
                  p.is_active
                    ? "bg-emerald-500/15 text-emerald-300 hover:bg-red-500/15 hover:text-red-300"
                    : "bg-white/5 text-white/55 hover:bg-emerald-500/15 hover:text-emerald-300"
                }`}
                data-testid="brand-discovery-product-toggle-btn"
              >
                {p.is_active ? "Hide" : "Show"}
              </button>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
};

export default BrandDiscoveryPage;
